import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faArrowRight, faUsers } from '@fortawesome/free-solid-svg-icons';
import CompanyLogo from './CompanyLogo';
import NewsletterLogo from './NewsletterLogo';
import { guessNewsletterDomain } from '../utils/newsletterLogo';
import '../css/components/HeroSponsorExamples.css';

interface ExampleSponsor {
    name: string;
    domain: string;
    tag: string;
    contactName?: string;
    contactTitle?: string;
    // Already partially masked, matching exactly what a logged-out visitor
    // sees elsewhere on the site (server/routes/sponsors.js's free-preview
    // endpoint, which is where this data was pulled from) — never a real,
    // fully-working address.
    maskedEmail: string;
    newsletterName: string;
    newsletterAudience: number;
}

// Real sponsors, real newsletter placements — exactly what a subscriber gets,
// pulled from the same public free-preview data the site already exposes to
// logged-out visitors. Not every real listing has a named contact captured,
// so one card intentionally shows just the (masked) partnership inbox —
// that's accurate, not a placeholder.
const EXAMPLE_SPONSORS: ExampleSponsor[] = [
    {
        name: 'WorkOS',
        domain: 'workos.com',
        tag: 'Software',
        contactName: 'Rory Duncan',
        contactTitle: 'Head of Business Development & Partnerships',
        maskedEmail: 'kev***@workos.com',
        newsletterName: 'TLDR',
        newsletterAudience: 750000,
    },
    {
        name: 'ArchaeologicalPaths',
        domain: 'archaeologicalpaths.com',
        tag: 'Travel',
        contactName: 'Magdalena Paszkiewicz',
        contactTitle: 'Marketing Manager',
        maskedEmail: 'gre***@archaeologicalpaths.com',
        newsletterName: 'Global Traveler USA',
        newsletterAudience: 800000,
    },
    {
        name: 'Brain.fm',
        domain: 'brain.fm',
        tag: 'Health',
        maskedEmail: 'pre***@brain.fm',
        newsletterName: 'Mark Manson',
        newsletterAudience: 1000000,
    },
];

const HeroSponsorExamples: React.FC = () => (
    <div className="hero-sponsor-examples">
        {EXAMPLE_SPONSORS.map((sponsor, i) => (
            <div key={sponsor.name} className={`hero-sponsor-card hero-sponsor-card--${i}`}>
                <div className="hero-sponsor-card__header">
                    <CompanyLogo domain={sponsor.domain} name={sponsor.name} size={28} />
                    <div className="hero-sponsor-card__name-tag">
                        <h3>{sponsor.name}</h3>
                        <span className="hero-sponsor-card__tag">{sponsor.tag}</span>
                    </div>
                </div>
                <div className="hero-sponsor-card__contact">
                    {sponsor.contactName && (
                        <p className="hero-sponsor-card__contact-name">{sponsor.contactName}</p>
                    )}
                    {sponsor.contactTitle && (
                        <p className="hero-sponsor-card__contact-title">{sponsor.contactTitle}</p>
                    )}
                    <p className="hero-sponsor-card__contact-email">
                        <FontAwesomeIcon icon={faEnvelope} /> {sponsor.maskedEmail}
                    </p>
                </div>
                {/* Summarized sponsorship line — who they sponsored, kept to one
                    row so the card stays scannable rather than turning into a
                    second full profile. */}
                <div className="hero-sponsor-card__sponsorship">
                    <FontAwesomeIcon icon={faArrowRight} className="hero-sponsor-card__sponsorship-icon" />
                    <NewsletterLogo domain={guessNewsletterDomain(sponsor.newsletterName)} size={18} />
                    <span className="hero-sponsor-card__newsletter-name">{sponsor.newsletterName}</span>
                    <span className="hero-sponsor-card__newsletter-audience">
                        <FontAwesomeIcon icon={faUsers} /> {(sponsor.newsletterAudience / 1000).toFixed(0)}K
                    </span>
                </div>
            </div>
        ))}
    </div>
);

export default HeroSponsorExamples;
